import { useState } from 'react';
import { ClipboardList } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Card, CardContent } from '@/components/ui/Card';
import { Pagination } from '@/components/ui/Pagination';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/Table';

const formatarData = (valor: string | Date) =>
  new Date(valor).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

/**
 * Visão consolidada para a administração montar as diretorias.
 * Quem nunca respondeu continua na lista, marcado como pendente.
 */
export function AptidaoResultadosPage() {
  const [page, setPage] = useState(1);
  const [diretoriaId, setDiretoriaId] = useState('');
  const [somenteRespondidos, setSomenteRespondidos] = useState(false);

  const diretorias = trpc.aptidao.listarDiretorias.useQuery();
  const lista = trpc.aptidao.listarResultados.useQuery({
    page,
    limit: 20,
    diretoriaId: diretoriaId || undefined,
    somenteRespondidos,
  });

  const aoTrocarFiltro = (valor: string) => {
    setDiretoriaId(valor);
    setPage(1);
  };

  const aoTrocarPendentes = (valor: boolean) => {
    setSomenteRespondidos(valor);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-6 w-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Resultados do Teste de Aptidão</h1>
        </div>
        <p className="text-sm text-gray-600">
          Afinidade principal de cada companheiro, considerando sempre a tentativa mais recente.
        </p>
      </header>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-4 py-4">
          <div className="min-w-[220px] flex-1">
            <label htmlFor="filtro-diretoria" className="mb-1 block text-sm font-medium text-gray-700">
              Diretoria
            </label>
            <select
              id="filtro-diretoria"
              value={diretoriaId}
              onChange={(e) => aoTrocarFiltro(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">Todas</option>
              {(diretorias.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.emoji ? `${d.emoji} ` : ''}
                  {d.nome}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 pb-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={somenteRespondidos}
              onChange={(e) => aoTrocarPendentes(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Ocultar quem ainda não respondeu
          </label>
        </CardContent>
      </Card>

      {lista.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          Não foi possível carregar os resultados: {lista.error.message}
        </div>
      )}

      <Card>
        <CardContent className="px-0 py-0">
          {lista.isLoading ? (
            <p className="px-6 py-8 text-center text-gray-500">Carregando resultados…</p>
          ) : (lista.data?.items.length ?? 0) === 0 ? (
            <p className="px-6 py-8 text-center text-gray-500">
              Nenhum companheiro encontrado com esse filtro.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Companheiro</TableHead>
                  <TableHead>Diretoria de maior afinidade</TableHead>
                  <TableHead>Pontuação</TableHead>
                  <TableHead>Respondido em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(lista.data?.items ?? []).map((item) => (
                  <TableRow key={item.userId}>
                    <TableCell>
                      <span className="font-medium text-gray-900">{item.nome}</span>
                      <span className="block text-xs text-gray-500">{item.email}</span>
                    </TableCell>

                    <TableCell>
                      {item.topo ? (
                        <span>
                          {item.topo.emoji ? `${item.topo.emoji} ` : ''}
                          {item.topo.nome}
                        </span>
                      ) : (
                        <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                          Ainda não respondeu
                        </span>
                      )}
                    </TableCell>

                    <TableCell className="text-gray-600">
                      {item.topo ? `${item.topo.pontos} de 6 · ${item.topo.percentual}%` : '-'}
                    </TableCell>

                    <TableCell className="text-gray-600">
                      {item.respondidoEm ? formatarData(item.respondidoEm) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {lista.data && (
        <Pagination meta={lista.data.meta} onPageChange={setPage} itemLabel="companheiros" />
      )}
    </div>
  );
}
